import { FC, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { format, parseISO } from 'date-fns';
import { todayLocalISO } from '../utils/dateUtils';
import { useFormatInstant } from '../hooks/useClinicTimezone';
import { useIsOffline } from '../offline';
import { printHtmlDocument } from './Medication/printLabel';
import {
  inventoryService,
  LocationDailyReport,
  ReportActivityKind,
  StockLocation,
} from '../services/inventoryService';

interface TruckDailyReportDialogProps {
  open: boolean;
  onClose: () => void;
  location: StockLocation | null;
}

const KIND_LABELS: Record<ReportActivityKind, string> = {
  DISPENSE: 'Dispensed',
  TRANSFER_IN: 'Transferred in',
  TRANSFER_OUT: 'Transferred out',
  VOID_RESTORE: 'Returned (void)',
  RECEIVE: 'Received',
  COUNT: 'Counted',
  OTHER: 'Adjustment',
};

// A count corrects on-hand either way, so its amount keeps a sign; every other kind is an amount moved.
const activityQty = (a: { kind: ReportActivityKind; quantity: number }): string =>
  a.kind === 'COUNT' && a.quantity > 0 ? `+${a.quantity}` : `${a.quantity}`;

const KIND_COLORS: Record<ReportActivityKind, 'default' | 'error' | 'success' | 'info' | 'warning'> = {
  DISPENSE: 'error',
  TRANSFER_IN: 'success',
  TRANSFER_OUT: 'warning',
  VOID_RESTORE: 'info',
  RECEIVE: 'success',
  COUNT: 'info',
  OTHER: 'default',
};

/**
 * Local calendar day → [start, end) ISO instants. The server does no TZ math.
 *
 * ⚠ The window is the BROWSER'S day, while the activity times below render in
 * the CLINIC'S zone (DATE-KINDS §0: an instant renders in the clinic zone).
 * For a clinician standing in the clinic the two agree; for remote/relief
 * staff in another zone the window and the rendered times disagree by the
 * zone delta. Closing that needs a clinic-zone day→instant helper (the
 * inverse of `formatInstantInZone`), which is a write-side follow-up — not
 * a render fix.
 */
const dayBounds = (dateStr: string) => {
  const start = new Date(`${dateStr}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { fromIso: start.toISOString(), toIso: end.toISOString() };
};

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Build the printable count sheet as a self-contained HTML document and print
 * it through a hidden iframe (same isolation approach as printRxLabels — only
 * the report prints, never the app behind it). The "Physical count" blank
 * column is the point: dvm360's canonical truck ritual is print → count the
 * truck against the sheet → correct + restock to par.
 *
 * Runs outside React, so the clinic-zone formatter is passed in (the bound
 * `useFormatInstant()` from the dialog) rather than read from a hook.
 */
const printReport = (
  report: LocationDailyReport,
  dateLabel: string,
  formatInstant: (value: string, pattern: string) => string,
) => {
  const activityRows = report.activity
    .map(
      (a) => `<tr>
        <td>${formatInstant(a.at, 'HH:mm')}</td>
        <td>${esc(KIND_LABELS[a.kind] ?? a.typeRaw)}</td>
        <td>${esc(a.itemName)}</td>
        <td class="num">${activityQty(a)}</td>
        <td>${esc(a.actorName ?? '—')}</td>
        <td>${esc(a.patientName ?? '—')}</td>
      </tr>`,
    )
    .join('');

  const summaryRows = report.summary
    .map(
      (s) => `<tr>
        <td>${esc(s.itemName)}</td>
        <td class="num">${s.used}</td>
        <td class="num">${s.transferredIn}</td>
        <td class="num">${s.transferredOut}</td>
        <td class="num">${s.returned}</td>
        <td class="num">${s.received}</td>
        <td class="num">${(s.counted ?? 0) > 0 ? '+' : ''}${s.counted ?? 0}</td>
        <td class="num">${s.netChange > 0 ? '+' : ''}${s.netChange}</td>
        <td class="num">${s.onHand ?? '—'}</td>
        <td class="num">${s.parLevel ?? '—'}</td>
        <td class="count"></td>
      </tr>`,
    )
    .join('');

  const restockRows = report.restock
    .map(
      (r) => `<tr>
        <td>${esc(r.itemName)}</td>
        <td class="num">${r.onHand}</td>
        <td class="num">${r.reorderPoint ?? '—'}</td>
        <td class="num">${r.parLevel ?? '—'}</td>
        <td class="num"><strong>${r.shortfallToPar}</strong></td>
      </tr>`,
    )
    .join('');

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(report.location.name)} — Daily report ${dateLabel}</title>
<style>
  @page { margin: 14mm; }
  body { font: 12px/1.45 -apple-system, "Segoe UI", Arial, sans-serif; color: #222; }
  h1 { font-size: 17px; margin: 0 0 2px; }
  h2 { font-size: 13px; margin: 18px 0 6px; border-bottom: 1px solid #999; padding-bottom: 3px; }
  .meta { color: #555; margin-bottom: 4px; }
  table { border-collapse: collapse; width: 100%; margin-top: 4px; }
  th, td { border: 1px solid #bbb; padding: 4px 6px; text-align: left; }
  th { background: #f0ece5; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .count { width: 70px; background: #fff; }
  .empty { color: #777; font-style: italic; }
  .foot { margin-top: 16px; color: #777; font-size: 10px; }
  .sig { margin-top: 22px; display: flex; gap: 40px; }
  .sig div { flex: 1; border-top: 1px solid #555; padding-top: 3px; font-size: 11px; color: #555; }
</style></head><body>
  <h1>${esc(report.location.name)} — Daily Inventory Report</h1>
  <div class="meta">${dateLabel}${report.location.ownerVetName ? ` · Assigned vet: ${esc(report.location.ownerVetName)}` : ''} · ${report.totals.movements} movement(s), ${report.totals.totalUsedQty} unit(s) used</div>

  <h2>Usage &amp; count sheet</h2>
  ${
    report.summary.length
      ? `<table><thead><tr><th>Item</th><th>Used</th><th>In</th><th>Out</th><th>Returned</th><th>Received</th><th>Counted</th><th>Net</th><th>On hand</th><th>Par</th><th>Physical count</th></tr></thead><tbody>${summaryRows}</tbody></table>`
      : '<div class="empty">No stock activity in this period.</div>'
  }

  <h2>Restock to par</h2>
  ${
    report.restock.length
      ? `<table><thead><tr><th>Item</th><th>On hand</th><th>Reorder pt</th><th>Par</th><th>Transfer qty</th></tr></thead><tbody>${restockRows}</tbody></table>`
      : '<div class="empty">Nothing below par. Truck is stocked.</div>'
  }

  <h2>Activity</h2>
  ${
    report.activity.length
      ? `<table><thead><tr><th>Time</th><th>Action</th><th>Item</th><th>Qty</th><th>By</th><th>Patient</th></tr></thead><tbody>${activityRows}</tbody></table>`
      : '<div class="empty">No movements recorded.</div>'
  }

  <div class="sig"><div>Counted by / date</div><div>Restocked by / date</div></div>
  <div class="foot">Generated ${formatInstant(report.generatedAt, 'PPpp')} · StableTrack</div>
</body></html>`;

  // The hardened shared util (printLabel.ts) — handles the spurious
  // about:blank double-print race and never leaks the iframe.
  void printHtmlDocument(html);
};

/**
 * Daily activity report for one stock location (the truck ritual): what moved
 * today, per-item usage, and the below-par restock list — with a printable
 * count sheet. Read-only; ONLINE-ONLY (the movement ledger is never
 * replicated), so offline shows a loud notice instead of stale zeros.
 */
const TruckDailyReportDialog: FC<TruckDailyReportDialogProps> = ({ open, onClose, location }) => {
  const isOffline = useIsOffline();
  // INSTANTS in this dialog — `activity[].at` (StockMovement.createdAt,
  // DATE-KINDS §1.4) and `generatedAt` (`new Date()` server stamp) — render in
  // the CLINIC'S zone, never the browser's.
  const formatInstant = useFormatInstant();
  const [dateStr, setDateStr] = useState<string>(() => todayLocalISO());
  const { fromIso, toIso } = useMemo(() => dayBounds(dateStr), [dateStr]);

  const { data: report, isLoading, error } = useQuery<LocationDailyReport>(
    ['location-daily-report', location?.id, fromIso],
    () => inventoryService.getLocationDailyReport(location!.id, fromIso, toIso),
    { enabled: open && !!location && !isOffline },
  );

  // CALENDAR day: `dateStr` is the yyyy-MM-dd the user picked in the date
  // input. parseISO on a bare date is local midnight — the same day in every
  // zone — so the label never re-zones (DATE-KINDS §2.6: the old
  // `new Date(`${dateStr}T00:00:00`)` was the same value, spelled in the
  // idiom the ratchet cannot tell from the rollback bug).
  const dateLabel = useMemo(() => format(parseISO(dateStr), 'PPPP'), [dateStr]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {location ? `${location.name} — Daily report` : 'Daily report'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Date"
            type="date"
            size="small"
            value={dateStr}
            onChange={(e) => e.target.value && setDateStr(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {report?.location.ownerVetName && (
            <Typography variant="body2" color="text.secondary">
              Assigned vet: {report.location.ownerVetName}
            </Typography>
          )}
        </Box>

        {isOffline && (
          <Alert severity="info">
            You're offline — the daily report reads the live movement ledger and needs a connection.
          </Alert>
        )}
        {!isOffline && isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isOffline && !!error && (
          <Alert severity="error">Failed to load the report. Try again.</Alert>
        )}

        {!isOffline && report && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {report.totals.movements} movement(s) · {report.totals.totalUsedQty} unit(s) used across{' '}
              {report.totals.itemsUsed} item(s)
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Usage summary</Typography>
            {report.summary.length ? (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Used</TableCell>
                    <TableCell align="right">In</TableCell>
                    <TableCell align="right">Out</TableCell>
                    <TableCell align="right">Returned</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell align="right">Counted</TableCell>
                    <TableCell align="right">Net</TableCell>
                    <TableCell align="right">On hand</TableCell>
                    <TableCell align="right">Par</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.summary.map((s) => (
                    <TableRow key={s.inventoryItemId}>
                      <TableCell>{s.itemName}</TableCell>
                      <TableCell align="right">{s.used}</TableCell>
                      <TableCell align="right">{s.transferredIn}</TableCell>
                      <TableCell align="right">{s.transferredOut}</TableCell>
                      <TableCell align="right">{s.returned}</TableCell>
                      <TableCell align="right">{s.received}</TableCell>
                      <TableCell align="right">{(s.counted ?? 0) > 0 ? `+${s.counted}` : s.counted ?? 0}</TableCell>
                      <TableCell align="right">{s.netChange > 0 ? `+${s.netChange}` : s.netChange}</TableCell>
                      <TableCell align="right">{s.onHand ?? '—'}</TableCell>
                      <TableCell align="right">{s.parLevel ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No stock activity on this day.
              </Typography>
            )}

            <Typography variant="subtitle2">Restock to par</Typography>
            {report.restock.length ? (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">On hand</TableCell>
                    <TableCell align="right">Reorder pt</TableCell>
                    <TableCell align="right">Par</TableCell>
                    <TableCell align="right">Transfer qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.restock.map((r) => (
                    <TableRow key={r.inventoryItemId}>
                      <TableCell>{r.itemName}</TableCell>
                      <TableCell align="right">{r.onHand}</TableCell>
                      <TableCell align="right">{r.reorderPoint ?? '—'}</TableCell>
                      <TableCell align="right">{r.parLevel ?? '—'}</TableCell>
                      <TableCell align="right"><strong>{r.shortfallToPar}</strong></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nothing below par — the truck is stocked.
              </Typography>
            )}

            <Typography variant="subtitle2">Activity</Typography>
            {report.activity.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell>Patient</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.activity.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell data-testid="truck-activity-time">{formatInstant(a.at, 'HH:mm')}</TableCell>
                      <TableCell>
                        <Chip size="small" label={KIND_LABELS[a.kind] ?? a.typeRaw} color={KIND_COLORS[a.kind] ?? 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell>{a.itemName}</TableCell>
                      <TableCell align="right">{activityQty(a)}</TableCell>
                      <TableCell>{a.actorName ?? '—'}</TableCell>
                      <TableCell>{a.patientName ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No movements recorded on this day.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          disabled={!report || isOffline}
          onClick={() => report && printReport(report, dateLabel, formatInstant)}
        >
          Print count sheet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TruckDailyReportDialog;
